import { Link } from "react-router-dom";
import {
  FileText,
  Eye,
  DollarSign,
  Coins,
  Plus,
  LogOut,
  Pencil,
  EyeOff,
  Users,
  Settings,
} from "lucide-react";
import {
  useWriterDashboard,
  useWriterArticles,
  useSetArticlePublished,
} from "@/hooks/useWriter";
import { useAuth } from "@/context/useAuth";
import { StatsCard } from "@/components/writer/StatsCard";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import {
  truncateAddress,
  formatArticlePrice,
  formatRelativeDate,
} from "@/lib/format";
import { formatAtomicUnits } from "@/lib/x402";

export function DashboardPage() {
  const { data: dashboard, isLoading } = useWriterDashboard();
  const { data: articles, isLoading: articlesLoading } = useWriterArticles();
  const publishMutation = useSetArticlePublished();
  const { logout } = useAuth();

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
        <Skeleton className="h-20" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-display font-bold mb-2">
          Not onboarded yet
        </h2>
        <p className="text-grey font-body mb-6">
          Set up your writer profile to start publishing.
        </p>
        <Link to="/writer/onboard">
          <Button>Get Started</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Avatar
            src={dashboard.writer.avatarUrl}
            name={dashboard.writer.displayName}
            size="lg"
          />
          <div>
            <h1 className="text-2xl font-display font-bold">
              {dashboard.writer.displayName}
            </h1>
            {dashboard.writer.tagline && (
              <p className="text-sm text-grey font-body">
                {dashboard.writer.tagline}
              </p>
            )}
            <p className="text-sm text-grey font-body">
              {truncateAddress(dashboard.walletAddress)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/writer/profile/edit">
            <Button variant="ghost" size="sm" title="Edit Profile">
              <Settings size={16} />
            </Button>
          </Link>
          <Link to="/writer/articles/new">
            <Button size="sm">
              <Plus size={16} className="mr-1" /> New Article
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={logout}>
            <LogOut size={16} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <StatsCard
          label="Articles"
          value={dashboard.totalArticles}
          icon={<FileText size={20} className="text-grey" />}
        />
        <StatsCard
          label="Published"
          value={dashboard.publishedArticles}
          icon={<Eye size={20} className="text-grey" />}
        />
        <StatsCard
          label="Subscribers"
          value={dashboard.writer.subscriberCount}
          icon={<Users size={20} className="text-grey" />}
        />
        <StatsCard
          label="USDC Earned"
          value={`$${dashboard.totalEarnedUsdc ? formatAtomicUnits(dashboard.totalEarnedUsdc, 6) : "0.00"}`}
          icon={<DollarSign size={20} className="text-green" />}
        />
        <StatsCard
          label="EURC Earned"
          value={`€${dashboard.totalEarnedEurc ? formatAtomicUnits(dashboard.totalEarnedEurc, 6) : "0.00"}`}
          icon={<Coins size={20} className="text-blue" />}
        />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-display font-bold">Your Articles</h2>
        <Link to="/writer/articles">
          <Button variant="ghost" size="sm">
            View All
          </Button>
        </Link>
      </div>

      {articlesLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : !articles?.length ? (
        <Card className="p-10 text-center">
          <p className="text-grey font-body mb-4">
            You haven't written any articles yet.
          </p>
          <Link to="/writer/articles/new">
            <Button>Write Your First Article</Button>
          </Link>
        </Card>
      ) : (
        <Card className="divide-y divide-beige/10 px-4">
          {articles.slice(0, 5).map((article) => (
            <div
              key={article.id}
              className="flex items-start gap-4 py-4 last:border-0"
            >
              <div className="flex-1 min-w-0">
                <h4 className="font-body font-bold text-sm truncate">
                  {article.title}
                </h4>
                {article.previewMarkdown && (
                  <p className="text-xs text-grey font-body mt-1 line-clamp-2">
                    {article.previewMarkdown.slice(0, 120)}
                    {article.previewMarkdown.length > 120 ? "..." : ""}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant={article.published ? "success" : "default"}>
                    {article.published ? "Published" : "Draft"}
                  </Badge>
                  <span className="text-xs text-beige font-body">
                    {formatArticlePrice(article.priceUsdc, article.priceEurc)}
                  </span>
                  <span className="text-xs text-beige font-body">
                    {formatRelativeDate(article.updatedAt)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 pt-1">
                <Link to={`/writer/articles/${article.id}/edit`}>
                  <Button variant="ghost" size="sm">
                    <Pencil size={14} />
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    publishMutation.mutate({
                      id: article.id,
                      published: !article.published,
                    })
                  }
                  disabled={publishMutation.isPending}
                >
                  {article.published ? (
                    <EyeOff size={14} />
                  ) : (
                    <Eye size={14} />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
